import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { map, switchMap, withLatestFrom, mapTo, first } from 'rxjs/operators';
import { EnergiesService, InverterV2, MonitoringType, PowersService, EvChRTData, EvChDataV2, ProductModelService, HeatpumpService, HeatPumpRTData, HeatPumpDAY } from 'ngx-core-services';
import { format } from 'date-fns';
import { UiPromiseService } from 'ngx-zcs-components';
import { Workbook } from 'exceljs';

import { EvchargeTelemetryService } from '../../lib/services/evcharge-telemetry.service';

import {
  AppState,
  SystemsState,
  getSystemDefinitionConfiguration,
  requestTelemetryReport,
  selectedSystemMetadata,
  } from '..';
import { TelemetryReportMode } from '../../lib/telemetry-report/telemetry-report.component';
import { downloadExcel } from '../../lib/utils/export-xlsx';

interface PvResumeContent {
  [date: string]: {
    [serial: string]: Partial<InverterV2>[];
  };
}

interface EvchResumeContent {
  [date: string]: {
    [serial: string]: Partial<EvChRTData>[];
  };
}

interface HpResumeContent {
  [date: string]: {
    [serial: string]: Partial<HeatPumpRTData>[];
  };
}

interface HpAggregateContent {
  [serial: string]: Partial<HeatPumpDAY>[];
}

@Injectable()
export class TelemetryReportEffects {

  requestReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestTelemetryReport),
      switchMap((action) => {
        return this.store.select(selectedSystemMetadata).pipe(
          first(),
          mapTo(action),
        );
      }),
      withLatestFrom(this.store),
      map(([action, { systemState }]) => {
        this.exportReport(action.systemId, action.deviceIds, action.start, action.end, action.mode, systemState);
      })
    ),
    {
      dispatch: false,
    }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private uiPromiseService: UiPromiseService,
    private powersService: PowersService,
    private energiesService: EnergiesService,
    private evchargeTelemetryService: EvchargeTelemetryService,
    private heatpumpService: HeatpumpService
  ) {}

  async createXLSX(
    systemId: string,
    devicesIds: string[],
    gwType: MonitoringType,
    pvContent: PvResumeContent,
    evchContent: EvchResumeContent,
    hpContent: HpResumeContent,
    connextId?: string,
  ) {
    const workbook = new Workbook();

    // Helper to flatten PV rows for a single serial
    const flattenPv = () =>
      Object.entries(pvContent).flatMap(([date, devices]) =>
        Object.entries(devices).flatMap(([serial, rows]) =>
          rows
            .filter((d) => (gwType === 'Connext' ? d.GwId === connextId : d.GwId !== connextId))
            .map((d) => ({
              __kind: 'PV' as const,
              date,
              serial,
              ts: d.TS ? format(new Date(d.TS * 1000), 'yyyy-MM-dd HH:mm:ss') : '',
              tz: d.TZ,
              ft1: d.FT1,
              fc1: d.FC1,
              fr: d.FR,
              p_b1: d.P_B1,
              p_b2: d.P_B2,
              p_ext: d.P_EXT,
              bt: d.BT,
              bc: d.BC,
              bp: d.BP,
              bp1: d.BP1,
              bp2: d.BP2,
              bt1: d.BT1,
              p_self: d.P_SELF,
              p_dc_tot: d.P_DC_TOT,
              p_exp: d.P_EXP,
              p_load: d.P_LOAD,
              mp1: d.MP1,
              mp2: d.MP2,
              eg1: d.EG1,
              eg2: d.EG2,
              eg3: d.EG3,
              eg4: d.EG4,
              et1: d.ET1,
              et2: d.ET2,
              et3: d.ET3,
              et4: d.ET4,
              eg5: d.EG5,
              eg6: d.EG6,
              et5: d.ET5,
              et6: d.ET6,
              p_b_aggr: d.P_B_AGGR,
            })),
        ),
      );

    // Helper to flatten EVCH rows for a single serial
    const flattenEvch = () =>
      Object.entries(evchContent).flatMap(([date, devices]) =>
        Object.entries(devices).flatMap(([serial, rows]) =>
          rows
            .filter((d) => (gwType === 'Connext' ? d.GwId === connextId : d.GwId !== connextId))
            .map((d) => {
              const tsEpoch = d.TS;
              const tz = (d as any).TZ ?? '';
              return {
                __kind: 'EVCH' as const,
                date,
                serial,
                ts: tsEpoch ? format(new Date(tsEpoch * 1000), 'yyyy-MM-dd HH:mm:ss') : '',
                tz,
                ST: d.ST,
                TID: d.TID,
                CT: d.CT,
                EM: d.EM,
                SEM: d.SEM,
                CP: d.CP,
                SK: d.SK,
                created: d.created,
                PK: d.PK,
                TS: tsEpoch,
                GwId: d.GwId,
              };
            }),
        ),
      );

    // Helper to flatten Heat Pump rows for a single serial
    const flattenHp = () =>
      Object.entries(hpContent).flatMap(([date, devices]) =>
        Object.entries(devices).flatMap(([serial, rows]) =>
          rows
            .filter((d: any) => (gwType === 'Connext' ? d.GwId === connextId : d.GwId !== connextId))
            .map((d: any) => {
              const tsEpoch = d.TS;
              const tz = d.TZ ?? '';
              return {
                __kind: 'HP' as const,
                date,
                serial,
                ts: tsEpoch ? format(new Date(tsEpoch * 1000), 'yyyy-MM-dd HH:mm:ss') : '',
                tz,
                OUT_TEMP: d.OUT_TEMP,
                RET_TEMP: d.RET_TEMP,
                DHW_TEMP: d.DHW_TEMP,
                HP_E_PWR: d.HP_E_PWR,
                HP_E_EG: d.HP_E_EG,
                HP_E_ET: d.HP_E_ET,
                HP_T_PWR: d.HP_T_PWR,
                HP_T_EG: d.HP_T_EG,
                HP_T_ET: d.HP_T_ET,
                ENV_TEMP: d.ENV_TEMP,
                GwId: d.GwId,
              };
            }),
        ),
      );

    const allPvRows = flattenPv();
    const allEvchRows = flattenEvch();
    const allHpRows = flattenHp();

    for (const deviceId of devicesIds) {
      const productType = new ProductModelService(deviceId).type?.name;
      const isEvch = productType === 'evcharge';
      const isHeatpump = productType === 'heatPump';
      const worksheet = workbook.addWorksheet(deviceId);

      if (!isEvch && !isHeatpump) {
        worksheet.columns = [
          { header: 'Date', key: 'date', width: 15 },
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Timestamp', key: 'ts', width: 20 },
          { header: 'Timezone', key: 'tz', width: 10 },
          { header: 'AC Voltage R', key: 'ft1', width: 15 },
          { header: 'AC Current R', key: 'fc1', width: 15 },
          { header: 'Grid Frequency', key: 'fr', width: 15 },
          { header: 'Power Battery Pack 1', key: 'p_b1', width: 20 },
          { header: 'Power Battery Pack 2', key: 'p_b2', width: 20 },
          { header: 'Total Grid Power', key: 'p_ext', width: 20 },
          { header: 'Voltage Battery Pack', key: 'bt', width: 20 },
          { header: 'Current Battery Pack', key: 'bc', width: 20 },
          { header: 'SOC Battery Pack', key: 'bp', width: 20 },
          { header: 'SOC Battery Pack 1', key: 'bp1', width: 20 },
          { header: 'SOC Battery Pack 2', key: 'bp2', width: 20 },
          { header: 'Total Voltage Battery Pack 1', key: 'bt1', width: 25 },
          { header: 'Inverter Power Generation', key: 'p_self', width: 25 },
          { header: 'Total Power Generation', key: 'p_dc_tot', width: 25 },
          { header: 'Total Grid Power', key: 'p_exp', width: 20 },
          { header: 'Load Active Power', key: 'p_load', width: 20 },
          { header: 'DC Power PV1', key: 'mp1', width: 15 },
          { header: 'DC Power PV2', key: 'mp2', width: 15 },
          { header: 'Daily Production (Active)', key: 'eg1', width: 25 },
          { header: 'Daily Grid Feed-In', key: 'eg2', width: 20 },
          { header: 'Daily Energy Purchase', key: 'eg3', width: 25 },
          { header: 'Daily Consumption', key: 'eg4', width: 20 },
          { header: 'Cumulative Production', key: 'et1', width: 25 },
          { header: 'Cumulative Grid Feed-In', key: 'et2', width: 25 },
          { header: 'Cumulative Energy Purchase', key: 'et3', width: 25 },
          { header: 'Cumulative Consumption', key: 'et4', width: 25 },
          { header: 'Daily Charging Energy', key: 'eg5', width: 25 },
          { header: 'Daily Discharging Energy', key: 'eg6', width: 25 },
          { header: 'Total Charging Energy', key: 'et5', width: 25 },
          { header: 'Total Discharging Energy', key: 'et6', width: 25 },
          { header: 'Total Battery Power', key: 'p_b_aggr', width: 20 },
        ];
        const rows = allPvRows.filter((r) => r.serial === deviceId);
        rows.forEach((row) => worksheet.addRow(row));
      } else if (isEvch) {
        worksheet.columns = [
          { header: 'Date', key: 'date', width: 15 },
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Timestamp', key: 'ts', width: 20 },
          { header: 'Timezone', key: 'tz', width: 10 },
          { header: 'Status', key: 'ST', width: 12 },
          { header: 'Transaction ID', key: 'TID', width: 20 },
          { header: 'Charging time (minute)', key: 'CT', width: 12 },
          { header: 'Total Energy Charged', key: 'EM', width: 18 },
          { header: 'Session Energy Charged', key: 'SEM', width: 18 },
          { header: 'Charging Power', key: 'CP', width: 16 },
          { header: 'Session Key', key: 'SK', width: 18 },
          { header: 'Created (epoch s)', key: 'created', width: 18 },
          { header: 'PK', key: 'PK', width: 12 },
          { header: 'TS (epoch s)', key: 'TS', width: 16 },
          { header: 'Gateway ID', key: 'GwId', width: 24 },
        ];
        const rows = allEvchRows.filter((r) => r.serial === deviceId);
        rows.forEach((row) => worksheet.addRow(row));
      } else if (isHeatpump) {
        worksheet.columns = [
          { header: 'Date', key: 'date', width: 15 },
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Timestamp', key: 'ts', width: 20 },
          { header: 'Timezone', key: 'tz', width: 10 },
          { header: 'OUT Temp', key: 'OUT_TEMP', width: 15 },
          { header: 'RET Temp', key: 'RET_TEMP', width: 15 },
          { header: 'DHW Temp', key: 'DHW_TEMP', width: 15 },
          { header: 'HP Electric Power', key: 'HP_E_PWR', width: 20 },
          { header: 'HP Electric Daily Energy', key: 'HP_E_EG', width: 24 },
          { header: 'HP Electric Total Energy', key: 'HP_E_ET', width: 24 },
          { header: 'HP Thermal Power', key: 'HP_T_PWR', width: 20 },
          { header: 'HP Thermal Daily Energy', key: 'HP_T_EG', width: 24 },
          { header: 'HP Thermal Total Energy', key: 'HP_T_ET', width: 24 },
          { header: 'Environment Temp', key: 'ENV_TEMP', width: 20 },
          { header: 'Gateway ID', key: 'GwId', width: 24 },
        ];
        const rows = allHpRows.filter((r) => r.serial === deviceId);
        rows.forEach((row) => worksheet.addRow(row));
      }
    }

    const fileName = `report-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    await downloadExcel(workbook, fileName);
  }

  async createAggregateXLSX(
    systemId: string,
    devicesIds: string[],
    pvContent: { [serial: string]: Partial<InverterV2>[] },
    evchContent: { [serial: string]: Partial<EvChDataV2>[] },
    hpContent: HpAggregateContent,
  ) {
    const workbook = new Workbook();

    const flattenPv = Object.entries(pvContent).flatMap(([serial, rows]) =>
      rows.map((d) => ({
        __kind: 'PV' as const,
        serial,
        date: d.TS ? format(new Date(d.TS * 1000), 'yyyy-MM-dd') : '',
        eg1: d.EG1, eg2: d.EG2, eg3: d.EG3, eg4: d.EG4,
        eg5: d.EG5, eg6: d.EG6,
        et1: d.ET1, et2: d.ET2, et3: d.ET3, et4: d.ET4, et5: d.ET5, et6: d.ET6,
      }))
    );

    const flattenEvch = Object.entries(evchContent).flatMap(([serial, rows]) =>
      rows.map((d) => ({
        __kind: 'EVCH' as const,
        serial,
        date: d.TS ? format(new Date(d.TS * 1000), 'yyyy-MM-dd') : '',
        created: d.created,
        PK: d.PK,
        TS: d.TS,
        SK: d.SK,
        GwId: d.GwId,
        EM: d.EM,
        DEM: d.DEM,
        DCT: d.DCT,
        CC: d.CC,
      }))
    );

    const flattenHp = Object.entries(hpContent).flatMap(([serial, rows]) =>
      rows.map((d: any) => ({
        __kind: 'HP' as const,
        serial,
        date: d.TS ? format(new Date(d.TS * 1000), 'yyyy-MM-dd') : '',
        HP_E_EG: d.HP_E_EG,
        HP_E_ET: d.HP_E_ET,
        HP_T_EG: d.HP_T_EG,
        HP_T_ET: d.HP_T_ET,
      }))
    );

    for (const deviceId of devicesIds) {
      const productType = new ProductModelService(deviceId).type?.name;
      const isEvch = productType === 'evcharge';
      const isHeatpump = productType === 'heatPump';
      const sheet = workbook.addWorksheet(deviceId);

      if (!isEvch && !isHeatpump) {
        sheet.columns = [
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Date', key: 'date', width: 20 },
          { header: 'Daily Production (kwh)', key: 'eg1', width: 25 },
          { header: 'Daily Grid Feed-In (kwh)', key: 'eg2', width: 25 },
          { header: 'Daily Energy Purchase (kwh)', key: 'eg3', width: 25 },
          { header: 'Daily Consumption (kwh)', key: 'eg4', width: 25 },
          { header: 'Daily Charging Energy (kwh)', key: 'eg5', width: 25 },
          { header: 'Daily Discharging Energy (kwh)', key: 'eg6', width: 25 },
          { header: 'Cumulative Production (kwh)', key: 'et1', width: 25 },
          { header: 'Cumulative Grid Feed-In (kwh)', key: 'et2', width: 25 },
          { header: 'Cumulative Energy Purchase (kwh)', key: 'et3', width: 25 },
          { header: 'Cumulative Consumption (kwh)', key: 'et4', width: 25 },
          { header: 'Total Charging Energy (kwh)', key: 'et5', width: 25 },
          { header: 'Total Discharging Energy (kwh)', key: 'et6', width: 25 },
        ];
        const rows = flattenPv.filter((r) => r.serial === deviceId);
        rows.forEach((r) => sheet.addRow(r));
      } else if (isEvch) {
        sheet.columns = [
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Date', key: 'date', width: 15 },
          { header: 'Created (epoch s)', key: 'created', width: 18 },
          { header: 'PK', key: 'PK', width: 12 },
          { header: 'TS (epoch s)', key: 'TS', width: 16 },
          { header: 'Session Key', key: 'SK', width: 18 },
          { header: 'Gateway ID', key: 'GwId', width: 24 },
          { header: 'Total Energy (EM)', key: 'EM', width: 18 },
          { header: 'Daily Energy (DEM)', key: 'DEM', width: 18 },
          { header: 'Daily Charge Time (DCT)', key: 'DCT', width: 22 },
          { header: 'Charge Count (CC)', key: 'CC', width: 16 },
        ];
        const rows = flattenEvch.filter((r) => r.serial === deviceId);
        rows.forEach((r) => sheet.addRow(r));
      } else if (isHeatpump) {
        sheet.columns = [
          { header: 'Serial', key: 'serial', width: 20 },
          { header: 'Date', key: 'date', width: 20 },
          { header: 'HP Electric Daily Energy (HP_E_EG)', key: 'HP_E_EG', width: 32 },
          { header: 'HP Electric Total Energy (HP_E_ET)', key: 'HP_E_ET', width: 32 },
          { header: 'HP Thermal Daily Energy (HP_T_EG)', key: 'HP_T_EG', width: 32 },
          { header: 'HP Thermal Total Energy (HP_T_ET)', key: 'HP_T_ET', width: 32 },
        ];
        const rows = flattenHp.filter((r) => r.serial === deviceId);
        rows.forEach((r) => sheet.addRow(r));
      }
    }

    const fileName = `report-aggregate-${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    await downloadExcel(workbook, fileName);
  }

  exportReport(systemId: string, devicesIds: string[], start: Date, end: Date,  mode: TelemetryReportMode, systemState: SystemsState) {
    const system = systemState.systemList[systemId];

    if (!system) {
      throw new Error(`System with id ${systemId} not found`);
    }

    const systemConfiguration = getSystemDefinitionConfiguration(system);
    const connextId = system.configuration.connextId || '';

    this.uiPromiseService.run(async () => {
      if (!start || !end) return;

      const pvContent: PvResumeContent = {};
      const evchContent: EvchResumeContent = {};
      const hpContent: HpResumeContent = {};

      const useOnlyNetwork = false;

      // Split devices by kind
      const evchDevices = (devicesIds || []).filter((id) => new ProductModelService(id).type?.name === 'evcharge');
      const hpDevices = (devicesIds || []).filter((id) => new ProductModelService(id).type?.name === 'heatPump');
      const pvDevices = (devicesIds || []).filter((id) => !evchDevices.includes(id) && !hpDevices.includes(id));

      if (mode === TelemetryReportMode.FiveMinuteTelemetry) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const date = format(d, 'yyyy-MM-dd');
          if (pvDevices.length) {
            pvContent[date] = await this.powersService.fetchFullTelemetry(pvDevices, date, useOnlyNetwork, `${ systemConfiguration.tz }`);
          }
          if (evchDevices.length) {
            evchContent[date] = await this.evchargeTelemetryService.fetchEvchargeDailyData(evchDevices, date, `${ systemConfiguration.tz }`, useOnlyNetwork, undefined, undefined);
          }
          if (hpDevices.length) {
            hpContent[date] = await this.heatpumpService.fetchHeatpumpsRange(
              hpDevices,
              date,
              `${ systemConfiguration.tz }`,
              undefined,
            );
          }
        }
        await this.createXLSX(systemId, devicesIds, systemConfiguration.monitoringType, pvContent, evchContent, hpContent, connextId);
      } else if (mode === TelemetryReportMode.DailyAggregate) {
        const from = format(start, 'yyyy-MM-dd');
        const to = format(end, 'yyyy-MM-dd');

        const pvAgg = pvDevices.length ? await this.energiesService.fetchEnergiesByRange(pvDevices, systemConfiguration.monitoringType, { from, to }, useOnlyNetwork) : {};
        const evchAgg = evchDevices.length ? await this.evchargeTelemetryService.fetchEvchargeAggregation(evchDevices, { from, to }, `${ systemConfiguration.tz }`) : {};
        const hpAgg: HpAggregateContent = hpDevices.length
          ? await this.heatpumpService.fetchHeatpumpsAggregation(hpDevices, { from, to }, `${ systemConfiguration.tz }`)
          : {};

        await this.createAggregateXLSX(systemId, devicesIds, pvAgg, evchAgg, hpAgg);
      }

      return EMPTY;
    }, {
      loading: true,
      loadingMessage: 'Exporting report...',
    });
  }
}
